import { useState } from 'react'
import { Select } from '@lunary/ui-web/select'
import type { StaffNotificationRecipientDto, StaffNotificationTestResponseDto, UpdateStaffNotificationSettingsDto } from '@lunary/api-client'
import { EVENT_KEYS, ROLE_LABELS, SCOPE_OPTIONS, eventHint, eventLabel } from './events'
import type { EventLimits, StaffEventKey, StaffScope } from './events'
import { notificationsErrorText, useSaveNotificationSettings, useTestNotification } from './queries'
import expert from '../../shared/ui/expert.module.css'
import styles from './notifications.module.css'

type Draft = { events: StaffEventKey[]; scope: StaffScope; quietOn: boolean; quietFrom: string; quietTo: string; urgent: boolean }

const TEST_TEXT: Readonly<Record<StaffNotificationTestResponseDto['result'], string>> = {
  sent: '✅ Отправили, проверьте Telegram',
  not_started: 'Бот не может написать: откройте бота Lunary в Telegram и нажмите /start',
  failed: 'Telegram не принял сообщение, попробуйте позже',
  disabled: 'Отправка выключена: на сервере не настроен бот',
}

function draftOf(r: StaffNotificationRecipientDto): Draft {
  return {
    events: [...r.events],
    scope: r.scope,
    quietOn: r.quiet_from !== null && r.quiet_to !== null,
    quietFrom: r.quiet_from ?? '23:00',
    quietTo: r.quiet_to ?? '08:00',
    urgent: r.urgent_new_orders,
  }
}

function bodyOf(d: Draft): UpdateStaffNotificationSettingsDto {
  return {
    events: EVENT_KEYS.filter((k) => d.events.includes(k)),
    scope: d.scope,
    quiet_from: d.quietOn ? d.quietFrom : null,
    quiet_to: d.quietOn ? d.quietTo : null,
    urgent_new_orders: d.urgent,
  }
}

type Props = { recipient: StaffNotificationRecipientDto; limits: EventLimits }

export function RecipientCard({ recipient, limits }: Props) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(recipient))
  const save = useSaveNotificationSettings()
  const test = useTestNotification()
  const set = (patch: Partial<Draft>) => {
    save.reset()
    setDraft((d) => ({ ...d, ...patch }))
  }
  const body = bodyOf(draft)
  const dirty = JSON.stringify(body) !== JSON.stringify(bodyOf(draftOf(recipient)))
  const quietInvalid = draft.quietOn && (!draft.quietFrom || !draft.quietTo || draft.quietFrom === draft.quietTo)
  const name = recipient.account?.display_name ?? (recipient.account?.telegram_username ? `@${recipient.account.telegram_username}` : ROLE_LABELS[recipient.role])
  const toggle = (key: StaffEventKey) =>
    set({ events: draft.events.includes(key) ? draft.events.filter((k) => k !== key) : [...draft.events, key] })

  return (
    <section className={expert.panel} aria-labelledby={`recipient-${recipient.telegram_id}`}>
      <div className={expert.panelHead}>
        <h2 id={`recipient-${recipient.telegram_id}`}>{name}</h2>
        <span className={styles.badge}>{ROLE_LABELS[recipient.role]}</span>
        <span className={`${styles.badge} ${styles.mono}`} title="Telegram ID">
          {recipient.masked_id}
        </span>
        {recipient.self && <span className={`${styles.badge} ${styles.self}`}>Это вы</span>}
      </div>
      {!recipient.account && <p className="muted">Этот Telegram не связан с аккаунтом Lunary: уведомления придут, но отметку «прочитано» админка поставить не сможет.</p>}

      <h3 className={styles.subhead}>Что присылать</h3>
      <ul className={styles.events}>
        {EVENT_KEYS.map((key) => (
          <li key={key} className={styles.event}>
            <span>
              <b>{eventLabel(key)}</b>
              <span className={styles.hint}>{eventHint(key, limits)}</span>
            </span>
            <button
              type="button"
              role="switch"
              className={styles.switch}
              aria-checked={draft.events.includes(key)}
              aria-label={eventLabel(key)}
              onClick={() => toggle(key)}
            />
          </li>
        ))}
      </ul>

      <div className={styles.grid}>
        <div className={styles.field}>
          <span className={styles.label}>Какие разборы</span>
          <Select size="sm" sheet="never" aria-label="Какие разборы" value={draft.scope} options={SCOPE_OPTIONS} onChange={(scope) => set({ scope })} />
          <span className={styles.hint}>«Свободные и мои» — без разборов, которые взял другой таролог.</span>
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Тихие часы, по Москве</span>
          <div className={styles.quiet}>
            <button
              type="button"
              role="switch"
              className={styles.switch}
              aria-checked={draft.quietOn}
              aria-label="Тихие часы"
              onClick={() => set({ quietOn: !draft.quietOn })}
            />
            <input type="time" aria-label="Начало тихих часов" value={draft.quietFrom} disabled={!draft.quietOn} onChange={(e) => set({ quietFrom: e.target.value })} />
            <span aria-hidden="true">–</span>
            <input type="time" aria-label="Конец тихих часов" value={draft.quietTo} disabled={!draft.quietOn} onChange={(e) => set({ quietTo: e.target.value })} />
          </div>
          <label className={styles.check}>
            <input type="checkbox" checked={draft.urgent} disabled={!draft.quietOn} onChange={(e) => set({ urgent: e.target.checked })} />
            <span>Новые заказы присылать и в тихие часы</span>
          </label>
          <span className={styles.hint}>Остальное придёт одной сводкой, когда тихие часы закончатся.</span>
        </div>
      </div>

      {quietInvalid && <p className="error-box">Начало и конец тихих часов должны различаться.</p>}
      {save.isError && (
        <p className="error-box" role="alert">
          {notificationsErrorText(save.error)}
        </p>
      )}
      <div className={styles.actions}>
        <button type="button" disabled={!dirty || quietInvalid || save.isPending} onClick={() => save.mutate({ telegramId: recipient.telegram_id, body })}>
          {save.isPending ? 'Сохраняем…' : save.isSuccess && !dirty ? 'Сохранено' : 'Сохранить'}
        </button>
        <button type="button" className="secondary" disabled={test.isPending} onClick={() => test.mutate(recipient.telegram_id)}>
          {test.isPending ? 'Отправляем…' : 'Тестовое уведомление'}
        </button>
        <span className={styles.status} aria-live="polite">
          {test.isSuccess ? TEST_TEXT[test.data.result] : test.isError ? notificationsErrorText(test.error) : ''}
        </span>
      </div>
    </section>
  )
}
